import './DodProductFilterTypePicker.scss';
import React, { useEffect, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { ByzzerChangeEventHandler } from '@byzzer/ui-components';
import {
    BRAND_AND_MANUFACTURER_INFO,
    CATEGORY_SELECTION_INFO,
    CHARACTERISTICS_INFO,
    CUSTOM_CHAR_INFO,
    HIERARCHY_INFO,
    PPG_INFO,
    SAVED_PRODUCT_INFO,
    UNIVERSAL_SAVED_SELECTION_PRODUCT_INFO
} from '@/views/DataOnDemand/Products/ProductConstants';
import { filterHasExplicitValues, filterValuesToStrings } from '@/components/DodConfigEditor/common/utils';
// BYZ-18245 - market characteristic invalidation lives in its own module to keep `utils` free of service imports
import { InvalidMarketCharacteristic } from '@/components/DodConfigEditor/common/marketCharacteristics';
import { useCharacteristicService } from '@/services/characteristic.service';
import { useUser } from '@/contexts/UserContext';
import {
    DodFilterTypeOptionGroup,
    DodFilterTypePicker, DodFilterTypePickerProps,
    DodFilterTypePickerRef,
} from '@/components/DodConfigEditor/common';
import { DodFilters } from '@/types/DodRun';
import { DodFilterType, DodProductFilterType } from '@/components/DodConfigEditor/types';
import classnames from 'classnames';
import { DodPreset } from '@/types/ApiTypes';
import { useTenantApi } from '@/hooks';
import { useCategoryService } from '@/services/category.service';
import { getPPGOptionsforCategories, PPGOption } from '@/services/ppg.service';
import { defaultPpgId, upcLevelPpgId, upcLevelPpgName } from '@/config/globalVars';
import { useDodWizard } from '@/components/DodConfigEditor/DodRunConfigWizard/DodWizardContext';
import useAllExceptProductFields from '@/utils/useAllExceptProductFields';

const baseClassName = 'dod-product-filter-type-picker';

export type DodProductFilterTypePickerProps = {
    name?: string;
    className?: string;
    value: DodFilterType<DodProductFilterType>;
    onChange: ByzzerChangeEventHandler<DodFilterType<DodProductFilterType>>;
    onPresetSelect: ByzzerChangeEventHandler<DodPreset>;
    filters: DodFilters;
    tipDelay?: number | [number | null, number | null];
    onNoCustomChars: () => void;
    onNoPpgs: () => void;
    allowedHierarchyLevels?: string[];
    onSelectionChange?: (e: ByzzerChangeEvent<string[] | 'all'>, fieldType?: any) => Promise<void>;
    onClearSelection?: ByzzerChangeEventHandler<DodFilters>;
    confirmMarketResetFn?: (
        marketNodes?: MarketNode[],
        invalidMarketCharacteristics?: InvalidMarketCharacteristic[]
    ) => Promise<boolean>;
    resetFactSelections?(filterType: string): void;
} & Partial<Omit<DodFilterTypePickerProps<DodFilters, DodFilterType<DodProductFilterType>>, 'initialOptionGroups'>>;

function valueMatcher(a: DodFilterType<DodProductFilterType>, b: DodFilterType<DodProductFilterType>): boolean {
    return a?.type === b?.type && a?.data === b?.data;
}

const EXCLUDED_CHARACTERISTICS = ['unicorn_subcategory', 'brand', 'brand_owner', 'brand_owner_high']; //BYZ-11227

export function DodProductFilterTypePicker({
    className,
    onChange,
    onSelectionChange,
    filters,
    onNoCustomChars,
    onNoPpgs,
    allowedHierarchyLevels,
    onClearSelection,
    confirmMarketResetFn,
    resetFactSelections,
    ...props
}: DodProductFilterTypePickerProps) {
    const { getCharacteristicsForCategories } = useTenantApi();
    const { getCustomCharacteristicsForCategories } = useCharacteristicService();
    const { getCategoriesBasedOnFilter } = useCategoryService();
    const {allExceptSelectionFields} = useDodWizard();
    const { categories: allExceptProductFieldsCategories } = useAllExceptProductFields(
        filters,
        allExceptSelectionFields
    );
    const [optionGroups] = useState<DodFilterTypeOptionGroup<DodFilters, DodFilterType<DodProductFilterType>>[]>([
        {
            label: 'Hierarchy',
            tip: HIERARCHY_INFO,
            options: [
                { label: 'Department', value: { type: 'departments' } },
                { label: 'Super Category', value: { type: 'superCategories' } },
                { label: 'Category', value: { type: 'categories' } },
                {
                    label: 'Subcategory',
                    value: { type: 'subcategories' },
                    disabled(filters) {
                        return (
                            (allowedHierarchyLevels && !allowedHierarchyLevels.includes('subcategory'))
                            || (filters?.categories.values.length === 0 && filters?.categories.summedSelections.length === 0)
                        );
                    },
                },
                {
                    label: 'UPC',
                    value: { type: 'upcs' },
                    disabled(filters) {
                        return (
                            (allowedHierarchyLevels && !allowedHierarchyLevels.includes('upc'))
                            || (filters?.categories.values.length === 0 && filters?.categories.summedSelections.length === 0)
                        );
                    },
                },
                {
                    label: 'Product Description',
                    value: { type: 'productDescriptions' },
                    disabled(filters) {
                        return (
                            (allowedHierarchyLevels && !allowedHierarchyLevels.includes('productDescription'))
                            || (filters?.categories.values.length === 0 && filters?.categories.summedSelections.length === 0)
                        );
                    },
                },
            ],
            disabledTip: CATEGORY_SELECTION_INFO,
        },
        {
            label: 'Brand & Manufacturer',
            tip: BRAND_AND_MANUFACTURER_INFO,
            options: [
                {
                    label: 'Parent Company',
                    value: { type: 'parentCompanies' },
                    disabled(filters) {
                        return (
                            (allowedHierarchyLevels && !allowedHierarchyLevels.includes('parentCompany')) 
                            || (filters?.categories.values.length === 0 && filters?.categories.summedSelections.length === 0)
                        );
                    },
                },
                {
                    label: 'Manufacturer',
                    value: { type: 'manufacturers' },
                    disabled(filters) {
                        return (
                            (allowedHierarchyLevels && !allowedHierarchyLevels.includes('manufacturer')) 
                            || (filters?.categories.values.length === 0 && filters?.categories.summedSelections.length === 0)
                        );
                    },
                },
                {
                    label: 'Brand',
                    value: { type: 'brands' },
                    disabled(filters) {
                        return (
                            (allowedHierarchyLevels && !allowedHierarchyLevels.includes('brand')) 
                            || (filters?.categories.values.length === 0 && filters?.categories.summedSelections.length === 0)
                        );
                    },
                },
            ],
            disabledTip: CATEGORY_SELECTION_INFO,
        },
        {
            key: 'characteristics',
            label: 'Characteristics',
            tip: CHARACTERISTICS_INFO,
            async loadOptions(_) {
                const categories = latestCategoriesRef.current;
                const characteristics = await getCharacteristicsForCategories(categories);
                return characteristics
                    .filter((char) => !EXCLUDED_CHARACTERISTICS.includes(char.code))
                    .map((char) => ({
                        label: char.displayName,
                        value: {
                            type: 'characteristics',
                            data: {
                                id: char.code,
                                displayName: char.displayName,
                            },
                        },
                    }));
            },
            disabled(filters) {
                return (allowedHierarchyLevels && !allowedHierarchyLevels.includes('characteristic')) || filters?.categories.values.length === 0 && filters?.categories.summedSelections.length === 0;
            },
            disabledTip: CATEGORY_SELECTION_INFO,
        },
        {
            key: 'customCharacteristics',
            label: 'My Custom Characteristics',
            tip: CUSTOM_CHAR_INFO,
            loadOptions(filters) {
                const categories = latestCategoriesRef.current;
                let characteristics: CustomCharacteristic[];
                if (filterHasExplicitValues(filters.categories)) {
                    characteristics = getCustomCharacteristicsForCategories(categories);
                } else {
                    if (
                        filters.categories.values === 'all' ||
                        filters.categories.summedSelections.some((sum) => sum.values === 'all')
                    ) {
                        characteristics = getCustomCharacteristicsForCategories(categories);
                    } else {
                        characteristics = [];
                    }
                }
                if(!characteristics.length) onNoCustomChars();
                return characteristics.map((char) => ({
                    label: char.label,
                    value: {
                        type: 'customCharacteristics',
                        data: {
                            id: char.id,
                            displayName: char.label,
                            // 'code' (char.id): In DodProductFilterBuilder -> 'handleSummedSelectionChange'-> else-if of isCharacteristicFilter(filterType), 'code' is expected be present but isn't needed.  Changing that to just check 'id'
                        },
                    },
                }));
            },
            disabled(filters) {
                return (allowedHierarchyLevels && !allowedHierarchyLevels.includes('customCharacteristic')) || filters?.categories.values.length === 0 && filters?.categories.summedSelections.length === 0;
            },
            disabledTip: CATEGORY_SELECTION_INFO,
        },
        {
            key: 'ppgs',
            label: 'My PPGs',
            tip: PPG_INFO,
            loadOptions(filters) {
                const categories = latestCategoriesRef.current;
                let ppgs: PPGOption[];
                if (filterHasExplicitValues(filters.categories)) {
                    ppgs = getPPGOptionsforCategories(categories);
                } else {
                    if (
                        filters.categories.values === 'all' ||
                        filters.categories.summedSelections.some((sum) => sum.values === 'all')
                    ) {
                        ppgs = getPPGOptionsforCategories(categories);
                    } else {
                        ppgs = [];
                    }
                }
                if(!ppgs.length) onNoPpgs();
                const defaultPpg: PPGOption = ppgs.find((ppg) => ppg.value === defaultPpgId) as PPGOption;
                const nonDefaultPpgs: PPGOption[] = ppgs.filter((ppg) => ppg.value !== defaultPpgId);
                return [defaultPpg, ...nonDefaultPpgs]
                    .filter((ppg) => ppg.value !== upcLevelPpgId && ppg.display !== upcLevelPpgName)
                    .map((ppg) => ({
                        label: ppg.display,
                        value: {
                            type: 'ppgs',
                            data: {
                                id: ppg.value,
                                displayName: ppg.display,
                            },
                        },
                    }));
            },
            disabled(filters) {
                return filters?.categories.values.length === 0 && filters?.categories.summedSelections.length === 0;
            },
            disabledTip: CATEGORY_SELECTION_INFO,
        }
    ]);
    const ref = useRef<DodFilterTypePickerRef>(null);
    const latestCategoriesRef = useRef<string[]>([]);

    // refresh the characteristics if and only if the categories change
    useEffect(() => {
        if(!isEqual(latestCategoriesRef.current, allExceptProductFieldsCategories)) {
            latestCategoriesRef.current = allExceptProductFieldsCategories;
            ref.current?.reloadOptionGroupsByKeys(['characteristics', 'customCharacteristics', 'ppgs']);
        }
    }, [allExceptProductFieldsCategories]);

    function handleChange(e: ByzzerChangeEvent<DodFilterType<DodProductFilterType>>) {
        onChange(e);
    }

    return (
        <DodFilterTypePicker
            className={classnames(baseClassName, className)}
            pickerRef={ref}
            filters={filters}
            initialOptionGroups={optionGroups}
            onChange={handleChange}
            onSelectionChange={onSelectionChange}
            onClearSelection={onClearSelection}
            valueMatcher={valueMatcher}
            presetType={'product'}
            presetGroupLabel={'My Saved Products'}
            presetGroupTip={SAVED_PRODUCT_INFO}
            filterPlaceholder={'Search Product Folders'}
            defaultExpandAll={true}
            showAllButton={true}
            confirmMarketResetFn={confirmMarketResetFn}
            resetFactSelections={resetFactSelections}
            dodWizardStep="product"
            universalSelectionDimensionType='product'
            universalSelectionDimensionGroupLabel='Byzzer Recommended Products'
            universalSelectionDimensionTip={UNIVERSAL_SAVED_SELECTION_PRODUCT_INFO}   
            {...props}
        />
    );
}

export default DodProductFilterTypePicker;
